import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, EmptyState, PageHeader } from "../../components/v3";
import { ExpenseReportForm, ExpenseReportResults } from "../../components/report";
import { AnalyticsService } from "../../services";
import { useHasPermission } from "../../utils";
import { GENERATE_EXPENSE_REPORT } from "../../constants/permissions";

export const ExpenseReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState(null);
  const [reportData, setReportData] = useState({});
  const [activeTab, setActiveTab] = useState("summary");
  const [paginationState, setPaginationState] = useState({ page: 0, pageSize: 10 });
  const hasPermission = useHasPermission(GENERATE_EXPENSE_REPORT);

  useEffect(() => {
    setReportData({});
  }, [activeTab]);

  const fetchData = async (filters, { onError } = {}) => {
    try {
      const { data } = await AnalyticsService.getExpenseReport(filters);
      setReportData(data);
    } catch (error) {
      onError?.();
      toast.error((await getReportRangeErrorMessage(error)) ?? (error?.response?.data?.message || "Failed to generate report"));
      console.error("Error fetching expense report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = (filters, { onError } = {}) => {
    setLoading(true);
    setReportData({});
    setReportFilters(filters);
    setPaginationState({ page: 0, pageSize: 10 });
    fetchData(
      {
        ...filters,
        page: 1,
        limit: 10,
      },
      { onError },
    );
  };

  const handlePaginationChange = (model) => {
    setLoading(true);
    setPaginationState(model);
    fetchData({
      ...reportFilters,
      page: model.page + 1,
      limit: model.pageSize,
    });
  };

  const reportType = [
    { label: "Summary", onClick: () => setActiveTab("summary") },
    { label: "Details", onClick: () => setActiveTab("details") },
  ];

  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader
          // title
          title="Expense Report"
          chipItems={reportType}
          activeTab={activeTab}
        />
      </Box>

      {/* form section */}
      <Box sx={{ mt: -7, zIndex: 2, position: "relative" }}>
        <ExpenseReportForm
          reportType={activeTab === "summary" ? "summary" : "detailed"}
          handleClear={() => setReportData({})}
          onGenerate={handleGenerate}
        />
      </Box>

      {/* results section */}
      {reportData?.count !== undefined &&
        (reportData?.count > 0 ? (
          <ExpenseReportResults
            reportType={activeTab === "summary" ? "summary" : "detailed"}
            data={reportData}
            loading={loading}
            paginationState={paginationState}
            handlePaginationChange={handlePaginationChange}
            showExportButton={hasPermission}
            reportFilters={reportFilters}
          />
        ) : (
          !loading && (
            <CardLayout>
              <EmptyState title="No Data Found" subtitle="No report found for selected filters!" />
            </CardLayout>
          )
        ))}
    </PageLayout>
  );
};
